<script lang="ts" setup>
import type { Check } from '~/types/checks'

const props = defineProps<{
  check: Check
}>()

const { t } = useNuxtApp().$i18n
const businessStore = useBusinessStore()
const { businessInfo } = storeToRefs(businessStore)

const cartItems = computed(() => {
  if (!props.check?.cart) return []
  const cart = typeof props.check.cart === 'string'
    ? JSON.parse(props.check.cart)
    : props.check.cart
  return (cart as any[]).filter(i => i.kitchen_status !== 'canceled')
})
</script>

<template>
  <div
    class="bg-white shadow-lg rounded-lg p-6 w-full max-w-xs font-mono text-sm"
    style="min-width: 320px;"
  >
    <!-- Business header -->
    <div class="text-center mb-4">
      <img
        v-if="businessInfo?.business_photo?.variants"
        :src="getImageBySize(businessInfo.business_photo.variants, 'ratioSmall')"
        class="mx-auto h-16 rounded mb-2"
        :alt="businessInfo?.name"
      >
      <p class="font-bold text-base">
        {{ businessInfo?.name }}
      </p>
    </div>

    <div class="border-t border-dashed border-gray-400 my-3" />

    <!-- Check info -->
    <div class="space-y-1 text-xs mb-3">
      <div class="flex justify-between">
        <span class="text-gray-500">{{ t('Check') }}</span>
        <span class="font-bold">#{{ check.check_number?.slice(-4) }}</span>
      </div>
      <div
        v-if="check.custom_label || check.table?.table_number"
        class="flex justify-between"
      >
        <span class="text-gray-500">{{ t('Table') }}</span>
        <span class="font-bold">{{ check.custom_label || check.table?.table_number }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500">{{ t('Date') }}</span>
        <span>{{ useDateTimeMedium(check.closed_at || check.created_at) }}</span>
      </div>
      <div
        v-if="check.customer_name"
        class="flex justify-between"
      >
        <span class="text-gray-500">{{ t('Customer') }}</span>
        <span>{{ check.customer_name }}</span>
      </div>
    </div>

    <div class="border-t border-dashed border-gray-400 my-3" />

    <!-- Items -->
    <ul class="space-y-1 text-xs mb-3 list-none p-0">
      <li
        v-for="(item, i) in cartItems"
        :key="i"
        class="flex justify-between"
      >
        <span>{{ item.quantity }}× {{ item.name }}</span>
        <span>{{ usePriceFormatter(item.total) }}</span>
      </li>
    </ul>

    <div class="border-t border-dashed border-gray-400 my-3" />

    <!-- Totals -->
    <div class="space-y-1 text-xs">
      <div class="flex justify-between">
        <span class="text-gray-500">{{ t('Subtotal') }}</span>
        <span>{{ usePriceFormatter(check.subtotal) }}</span>
      </div>
      <div
        v-if="check.tax"
        class="flex justify-between"
      >
        <span class="text-gray-500">{{ t('Tax') }}</span>
        <span>{{ usePriceFormatter(check.tax) }}</span>
      </div>
      <div
        v-if="check.tip"
        class="flex justify-between"
      >
        <span class="text-gray-500">{{ t('Tip') }}</span>
        <span>{{ usePriceFormatter(check.tip) }}</span>
      </div>
      <div
        v-if="check.discount"
        class="flex justify-between text-green-600"
      >
        <span>{{ t('Discount') }}</span>
        <span>-{{ usePriceFormatter(check.discount) }}</span>
      </div>
    </div>

    <!-- Payments -->
    <div class="border-t border-dashed border-gray-400 my-3" />
    <div class="space-y-1 text-xs">
      <template v-if="check.tenders?.length">
        <div
          v-for="tender in check.tenders"
          :key="tender.id"
          class="flex flex-col"
        >
          <div class="flex justify-between">
            <span class="text-gray-500">{{ tender.method_name }}</span>
            <span>{{ usePriceFormatter(tender.amount_tendered) }}</span>
          </div>
          <div
            v-if="tender.change_given > 0"
            class="flex justify-between pl-3 text-green-700"
          >
            <span>{{ t('Change') }}</span>
            <span>{{ usePriceFormatter(tender.change_given) }}</span>
          </div>
        </div>
      </template>
      <!-- Legacy fallback -->
      <div
        v-else-if="check.payment_method_name"
        class="flex justify-between"
      >
        <span class="text-gray-500">{{ t('Payment') }}</span>
        <span>{{ check.payment_method_name }}</span>
      </div>
    </div>

    <!-- Total -->
    <div class="flex justify-between font-bold text-sm border-t border-gray-300 pt-2 mt-3">
      <span>{{ t('Total') }}</span>
      <span>{{ usePriceFormatter(check.total) }}</span>
    </div>

    <div class="border-t border-dashed border-gray-400 my-3" />
    <p class="text-center text-xs text-gray-400">
      tapy.
    </p>
  </div>
</template>
